import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useCurrentUser, useWorkspace } from 'components/providers'
import { pick } from 'lodash-es'
import { useParams } from 'react-router'
import { DBProps, ITemplate, dbProps } from 'types'
import { getDocs, useWorkspaces } from 'utils'

export type TemplateRecord = Pick<ITemplate, keyof DBProps | (typeof partialTemplateFields)[number]>

const partialTemplateFields = ['description', 'title', 'workspaceId'] as const satisfies string[]

const makeQueryKey = (workspaceIds?: string[]) => ['templates', workspaceIds]

export function useTemplates(workspaceId?: string) {
  const workspaceIdParam = useParams().workspaceId
  const _workspaceId = workspaceId || workspaceIdParam
  const { workspaces } = useWorkspaces('emp', true)
  const workspaceIds =
    _workspaceId === 'all' ? workspaces?.map(w => w.id) : _workspaceId ? [_workspaceId] : []

  const { data, ...rest } = useQuery({
    queryKey: makeQueryKey(workspaceIds),
    queryFn: () => getTemplates(workspaceIds!),
    staleTime: 1000 * 30,
    enabled: !!workspaceIds?.length,
  })

  return { templates: data, ...rest }
}

export function useMutateTemplateRecords() {
  const user = useCurrentUser()
  const { workspace } = useWorkspace()
  const queryClient = useQueryClient()

  async function updateTemplateRecord(body: ITemplate) {
    if (!workspace) return

    const record = await makeTemplateRecord(body)

    queryClient.setQueriesData<TemplateRecord[]>(
      { queryKey: makeQueryKey([workspace.id]) },
      templates => {
        if (!templates) return [record]

        const templateIndex = templates.findIndex(e => e.id === record.id)
        if (templateIndex < 0) return sortTemplateRecords([...templates, record])

        return sortTemplateRecords(templates.map((e, i) => (i === templateIndex ? record : e)))
      }
    )
  }

  async function removeTemplateRecord(id: string) {
    if (!user || !workspace) return

    queryClient.setQueriesData<TemplateRecord[]>(
      { queryKey: makeQueryKey([workspace.id]) },
      templates => {
        if (!templates) return []

        const templateIndex = templates.findIndex(e => e.id === id)
        if (templateIndex < 0) return templates

        return templates.filter((_, i) => i !== templateIndex)
      }
    )
  }

  return { updateTemplateRecord, removeTemplateRecord }
}

async function getTemplates(workspaceIds: string[]) {
  const templates = await getDocs({
    collection: 'templates',
    fields: partialTemplateFields,
    constraints: [{ field: 'workspaceId', op: 'in', value: workspaceIds }],
  })

  return sortTemplateRecords(templates.data)
}

function sortTemplateRecords(records: TemplateRecord[]) {
  return records.sort((a, b) => a.title.localeCompare(b.title))
}

async function makeTemplateRecord(template: TemplateRecord) {
  const partial = pick(template, [...dbProps, ...partialTemplateFields])

  return { ...partial } as TemplateRecord
}
